import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, Boxes } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, booting } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (booting) return null;
  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav("/", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Invalid credentials");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FAFAFA] grid place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/kinos-logo.jpg" alt="KINOS" className="h-16 w-auto object-contain" />
          <p className="overline mt-4">Leading Innovation Technology</p>
        </div>
        <form
          onSubmit={submit}
          className="bg-white border border-border rounded-md p-7 space-y-5 shadow-sm"
        >
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">Sign in</h1>
            <p className="text-xs text-muted-foreground mt-1">
              KINOS Enterprise Resource Planning System
            </p>
          </div>
          <div>
            <Label className="overline">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@kinos.tl"
            />
          </div>
          <div>
            <Label className="overline">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {err && (
            <p data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {err}
            </p>
          )}
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-primary hover:bg-primary/90"
          >
            <LogIn className="size-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center mt-4">
            Powered by KINOS · 2026
          </p>
        </form>
      </div>
    </div>
  );
}
